import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { ContentTypeDefinition } from '@cms/contracts'
import { useI18n } from '../i18n/context.js'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import { createContent, getSite, listContentTypes } from '../api/endpoints.js'
import { useCan } from '../auth/session.js'
import { slugify } from '../domain/format.js'
import { contentPath, displayDomain } from '../domain/live-url.js'
import { resolveReason } from '../domain/reason.js'
import { paths } from '../routes.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { ReasonNote, SelectField, TextField } from '../ui/fields.js'
import { Banner, Button, Card, EmptyState, LinkButton, PageHeader, SkeletonLines } from '../ui/primitives.js'

/** `/s/:siteId/contenuti/nuovo`: what exists before there is a document — type, language, title, address. */
export function NewContentScreen() {
  const { t, locale: uiLocale } = useI18n()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { siteId, site } = useSiteWorkspace()
  const canWrite = useCan('cms:content:write')
  const [contentTypes, setContentTypes] = useState<ContentTypeDefinition[] | null>(null)
  const [siteLocales, setSiteLocales] = useState<string[]>([])
  const [contentTypeKey, setContentTypeKey] = useState(searchParams.get('type') ?? '')
  const [contentLocale, setContentLocale] = useState('')
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [optionsError, setOptionsError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    Promise.all([listContentTypes(), getSite(siteId).catch(() => null)])
      .then(([types, detail]) => {
        setContentTypes(types)
        setSiteLocales(detail?.locales ?? [])
        const enabled = detail?.configuration?.enabledContentTypeKeys ?? []
        setContentTypeKey((current) => current || (enabled[0] ?? types[0]?.key ?? ''))
        setContentLocale((current) => current || (detail?.defaultLocale ?? detail?.locales[0] ?? ''))
        setOptionsError(null)
      })
      .catch((err: unknown) => setOptionsError(err instanceof ApiError ? err.message : t('errors.network')))
  }, [siteId, t])

  const selectedType = useMemo(() => contentTypes?.find((type) => type.key === contentTypeKey), [contentTypes, contentTypeKey])
  const localeOptions = useMemo(() => {
    const fromType = selectedType?.locales ?? []
    const both = siteLocales.length ? fromType.filter((entry) => siteLocales.includes(entry)) : fromType
    return both.length ? both : fromType.length ? fromType : siteLocales
  }, [selectedType, siteLocales])

  useEffect(() => {
    if (localeOptions.length && !localeOptions.includes(contentLocale)) setContentLocale(localeOptions[0] ?? '')
  }, [localeOptions, contentLocale])

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(title))
  }, [title, slugTouched])

  const hasTitleField = !!selectedType?.fields.some((field) => field.key === 'title')
  const addressPreview = selectedType && slug ? contentPath(selectedType.urlPattern, {}, slug) : null
  const fallbackReason = t('reason.create', { slug: slug || '…' })

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault()
    if (!contentTypeKey || !contentLocale || !slug.trim()) {
      setError(t('edit.requiredFields'))
      return
    }
    setCreating(true)
    setError(null)
    try {
      const result = await createContent({
        siteId,
        contentTypeKey,
        locale: contentLocale,
        slug: slug.trim(),
        data: hasTitleField && title.trim() ? { title: title.trim() } : {},
        reason: resolveReason(note, fallbackReason),
        expectedVersion: 0,
      }, newIdempotencyKey())
      navigate(paths.content(siteId, result.id), { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'))
      setCreating(false)
    }
  }

  if (!canWrite) return <EmptyState icon="lock" title={t('edit.cannotCreate')} action={<LinkButton variant="outlined" to={paths.contents(siteId)}>{t('edit.backToList')}</LinkButton>}/>

  return <div className="page page-narrow">
    <PageHeader back={{ to: paths.contents(siteId), label: t('edit.backToList') }} eyebrow={site?.name} title={t('edit.newTitle')} subtitle={t('edit.newSubtitle')}/>
    {optionsError && <Banner tone="error" title={t('list.filtersErrorTitle')}>{optionsError}</Banner>}
    <form onSubmit={(event) => void handleCreate(event)}>
      <Card>
        {contentTypes === null ? <SkeletonLines count={4}/> : <div className="form-stack">
          <div className="form-grid">
            <SelectField label={t('list.filters.type')} value={contentTypeKey} onChange={(event) => setContentTypeKey(event.target.value)} required>
              {contentTypes.map((ct) => <option key={ct.key} value={ct.key}>{ct.label[uiLocale] ?? ct.label.it ?? ct.key}</option>)}
            </SelectField>
            <SelectField label={t('edit.locale')} value={contentLocale} onChange={(event) => setContentLocale(event.target.value)} required>
              {localeOptions.map((entry) => <option key={entry} value={entry}>{entry.toUpperCase()}</option>)}
            </SelectField>
          </div>
          <TextField label={t('edit.titleLabel')} value={title} onChange={(event) => setTitle(event.target.value)} supporting={hasTitleField ? t('edit.titleHelp') : t('edit.titleNotStored')} autoFocus/>
          <TextField
            label={t('edit.slug')}
            mono
            required
            value={slug}
            onChange={(event) => { setSlugTouched(true); setSlug(event.target.value) }}
            supporting={addressPreview ? t('edit.addressPreview', { address: `${displayDomain(site?.primaryDomain)}${addressPreview}` }) : t('edit.slugHelp')}
          />
          <ReasonNote value={note} onChange={setNote} fallback={fallbackReason} disabled={creating}/>
          {error && <Banner tone="error">{error}</Banner>}
          <div className="form-actions">
            <LinkButton variant="text" to={paths.contents(siteId)}>{t('common.cancel')}</LinkButton>
            <Button type="submit" variant="filled" icon="add" disabled={creating || !contentTypeKey || !slug.trim()}>{creating ? t('edit.creating') : t('edit.create')}</Button>
          </div>
        </div>}
      </Card>
    </form>
  </div>
}
